import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/apiClient";
import { PageHeader, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowsClockwise, Gear, Warning } from "@phosphor-icons/react";

export default function BalanceSheet() {
  const today = new Date().toISOString().slice(0, 10);
  const [asOf, setAsOf] = useState(today);
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/finance/balance-sheet", { params: { as_of: asOf } });
    setD(data);
  }, [asOf]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div data-testid="balance-sheet-page">
      <PageHeader
        title="Balance Sheet"
        module="Finance · FI"
        subtitle={`Snapshot of Assets, Liabilities and Equity as of ${asOf}.  Opening balances from ${d.opening_as_of || "not set"}.`}
        ribbon={
          <>
            <BcRibbonGroup label="Process">
              <BcRibbonBtn icon={ArrowsClockwise} label="Refresh" onClick={load} testid="ribbon-refresh" />
            </BcRibbonGroup>
            <BcRibbonGroup label="Setup">
              <Link to="/finance/opening-balances">
                <BcRibbonBtn icon={Gear} label="Opening Balances" testid="ribbon-opening-balances" />
              </Link>
            </BcRibbonGroup>
          </>
        }
      />

      <div className="p-6 space-y-5">
        <div className="flex items-end gap-3">
          <div>
            <Label className="label-caps mb-1 block">As of date</Label>
            <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="w-44" data-testid="bs-asof-input" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section title="Assets" lines={d.assets} total={d.total_assets} totalLabel="Total Assets" tone="assets" testid="bs-assets" />
          <div className="space-y-4">
            <Section title="Liabilities" lines={d.liabilities} total={d.total_liabilities} totalLabel="Total Liabilities" tone="liab" testid="bs-liab" />
            <Section title="Equity" lines={d.equity} total={d.total_equity} totalLabel="Total Equity" tone="equity" testid="bs-equity" />

            <div className="bc-card p-4 bg-[#0F172A] text-white">
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-400">Liabilities + Equity</div>
                  <div className="font-display text-[24px] font-bold font-mono-num mt-1 text-[#EA580C]">{fmtMoney(d.total_liab_plus_equity)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-400">Balance check</div>
                  <div className={`font-display text-[14px] font-semibold mt-1 ${Math.abs(d.balance_diff) < 1 ? "text-emerald-400" : "text-amber-400"}`}>
                    {Math.abs(d.balance_diff) < 1 ? "Balanced ✓" : `Δ ${fmtMoney(d.balance_diff)}`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {Math.abs(d.balance_diff) >= 1 && (
          <div className="bc-card p-3 bg-[#FFF4CE] border-[#F7C948] text-[#8A6E00] flex items-center gap-2 text-[12.5px]" data-testid="bs-balance-warning">
            <Warning size={14} weight="fill" />
            Balance sheet is off by <b className="mx-1">{fmtMoney(d.balance_diff)}</b>. This usually means opening balances (Cash / Capital / Loans) haven&apos;t been entered — click <Link to="/finance/opening-balances" className="text-[#2E75B6] underline">Opening Balances</Link> to reconcile.
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, lines, total, totalLabel, tone, testid }) {
  const totalColor = {
    assets: "text-emerald-700",
    liab: "text-red-700",
    equity: "text-[#2E75B6]",
  }[tone] || "text-[#201F1E]";
  return (
    <div className="bc-card overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b border-[#EDEBE9] bg-[#FAF9F8]">
        <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#605E5C]">Section</div>
        <div className="font-display text-[15px] font-semibold text-[#201F1E]">{title}</div>
      </div>
      <table className="w-full text-[13px]">
        <tbody className="font-mono-num">
          {lines.map((l) => (
            <tr key={l.label} className="border-b border-[#EDEBE9]">
              <td className="px-4 py-2 text-[#323130]">{l.label}</td>
              <td className={`px-4 py-2 text-right ${l.amount < 0 ? "text-red-700" : "text-[#323130]"}`}>{fmtMoney(l.amount)}</td>
            </tr>
          ))}
          <tr className="bg-[#FAF9F8]">
            <td className="px-4 py-3 font-display font-bold text-[14px] text-[#201F1E]">{totalLabel}</td>
            <td className={`px-4 py-3 text-right font-display font-bold text-[14px] font-mono-num ${totalColor}`}>{fmtMoney(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
